import { CommandFactory, CustomPropertyViewModelDecorator, DateDecorator, DateTimeMetaData, DateTimePropertyViewModel, MessageResourceManager, PropertyViewModelFactory, ServiceResponse, ToastMessageService, UIResultCommandInterface } from "@nts/std";
import { LicenseReportApiClient } from "../api-clients/license-report.api-client";
import { BehaviorSubject, lastValueFrom, map, merge, Observable, Subject } from "rxjs";
import { UsageReportFiltersDto } from "../dto/usage-report-filters.dto";
import { HttpEvent, HttpEventType } from "@angular/common/http";


export class LicenseReportViewModel {

    onActionInProgress = new Subject<boolean>();
    changed = new Subject<void>();


    resultCostReviewDataCommand: UIResultCommandInterface<boolean>;


    files$ = new BehaviorSubject<Array<File>>([]);
    showProgressBar$ = new BehaviorSubject<boolean>(false);
    hoverColor = '#52d8f2';
    primaryColor = '#006fdd';
    txtFileDescription = null;
    hasUploadedData = false;
    errorText = MessageResourceManager.Current.getMessage('NotValidFormat');
    addFileText = MessageResourceManager.Current.getMessage('CMD_UploadModal_AddFile_DisplayName');
    uploadFileText = MessageResourceManager.Current.getMessage('CMD_UploadModal_UploadFile_DisplayName');
    removeFileText = MessageResourceManager.Current.getMessage('CMD_UploadModal_RemoveFile_DisplayName');
    imageUploadWrapHovered = false;
    hasError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(private apiClient: LicenseReportApiClient, private toastMessageService: ToastMessageService) {
        this.setupCommands();
    }

    private setupCommands() {

        this.resultCostReviewDataCommand = CommandFactory.createResultUICommand(async () => {
            this.resultCostReviewDataCommand.closeModal = false;
            this.showProgressBar$.next(true);
            this.onActionInProgress.next(true);
            this.changed.next();

            const costDTO = new UsageReportFiltersDto();
            costDTO.base64CsvContent = await this.convertFileToBase64(this.files$.value[0]);

            try {

                const response = await lastValueFrom(this.apiClient.getAzureUsageReport(costDTO)
                    .pipe(map((httpEvent: HttpEvent<Blob>) => {
                        if (httpEvent) {
                            if (httpEvent.type === HttpEventType.DownloadProgress) {
                                //this.progressBarValue = Math.round(100 * httpEvent.loaded / httpEvent.total);
                                // this.cd.detectChanges(); //TODO
                            } else if (httpEvent.type === HttpEventType.Response) {
                                const blob = httpEvent.body;

                                const link = document.createElement('a');
                                link.href = window.URL.createObjectURL(blob);
                                link.download = "RipartizioneCostiBCP.csv";
                                link.click();
                                this.resultCostReviewDataCommand.closeModal = true;
                            }
                        }
                        return httpEvent;
                    }))
                )

                if (response instanceof ServiceResponse) {
                    this.toastMessageService.showToastsFromResponse(response);
                }

            } catch (err) {
                console.log(err);
            } finally {
                this.onActionInProgress.next(false);
                this.showProgressBar$.next(false);
            }

            return true;

        },
            () => this.canUploadFile()
        );


        this.resultCostReviewDataCommand.displayName = this.uploadFileText
        this.resultCostReviewDataCommand.isDefault = true;

    }




    async initialize(args: any): Promise<void> {
        this.hoverColor = this.shade(this.primaryColor, 0.9);
    }


    reset() {
        this.hasUploadedData = false;
        this.files$.next([]);
    }

    async fileSelected(event) {

        const file: File = event.target.files[0];
        this.hasError$.next(!(await this.validateFile(file)));

        if (!this.hasError$.value) {
            this.hasUploadedData = true;
            this.txtFileDescription = '<div> <p>' + file.name + '</p> </div>';
            this.files$.next([file]);

        } else {
            this.reset();
        }

    }

    convertFileToBase64(file: File): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                let base64String = reader.result as string;
                if (base64String?.length > 0) {
                    // Rimuovi l'intestazione "data:mime/type;base64," se presente
                    base64String = base64String.split(',')[1];
                }
                resolve(base64String);
            };
            reader.onerror = (error) => {
                reject(error);
            };
            reader.readAsDataURL(file);
        });
    }


    shade(color: string, percent: number) {
        var f = parseInt(color.slice(1), 16),
            t = percent < 0 ? 0 : 255,
            p = percent < 0 ? percent * -1 : percent,
            R = f >> 16, G = f >> 8 & 0x00FF,
            B = f & 0x0000FF;
        return "#" + (0x1000000 + (Math.round((t - R) * p) + R) * 0x10000 + (Math.round((t - G) * p) + G) * 0x100 + (Math.round((t - B) * p) + B)).toString(16).slice(1);
    }

    async validateFile(file: File): Promise<boolean> {

        if (file && this.isCsv(file)) {

            let text = await this.readFileAsync(file) as String;

            //rimuovo \r e creo un array con tutte le righe
            let csv = text.replace(/(\r|\r)/gm, "").split("\n");
            //se è presente il separatore ','
            if (csv[0].includes(',')) {

                //controllo che ci siano più righe (header + almeno una riga di dati)
                if (csv.length >= 2) {

                    let header = csv[0].split(',');
                    //controllo che gli header siano giusti
                    //BillingAccountId,BillingAccountName,BillingPeriodStartDate,BillingPeriodEndDate,BillingProfileId,BillingProfileName,
                    // AccountOwnerId,AccountName,SubscriptionId,SubscriptionName,Date,Product,PartNumber,MeterId,ServiceFamily,MeterCategory,
                    // MeterSubcategory,MeterRegion,MeterName,Quantity,EffectivePrice,Cost,UnitPrice,BillingCurrency,ResourceLocation,
                    // AvailabilityZone,ConsumedService,ResourceId,ResourceName,ServiceInfo1,ServiceInfo2,AdditionalInfo,Tags,InvoiceSectionId,
                    // InvoiceSection,CostCenter,UnitOfMeasure,ResourceGroup,ReservationId,ReservationName,ProductOrderId,ProductOrderName,OfferId,
                    // IsAzureCreditEligible,Term,PublisherName,PlanName,ChargeType,Frequency,PublisherType,PricingModel,PayGPrice,InvoiceID
                    if (
                        this.orStringCompare(header[0], 'billingaccountid', 'IDAccountDiFatturazione') &&
                        this.orStringCompare(header[1], 'billingaccountname', 'NomeAccountDiFatturazione') &&
                        this.orStringCompare(header[2], 'billingperiodstartdate', 'DataInizioPeriodoFatturazione') &&
                        this.orStringCompare(header[3], 'billingperiodenddate', 'DataFinePeriodoFatturazione') &&
                        this.orStringCompare(header[4], 'billingprofileid', 'IDProfiloFatturazione') &&
                        this.orStringCompare(header[5], 'billingprofilename', 'NomeProfiloFatturazione') &&
                        this.orStringCompare(header[6], 'accountownerid', "ID del proprietario dell'account") &&
                        this.orStringCompare(header[7], 'accountname', "Nome dell'account") &&
                        this.orStringCompare(header[8], 'subscriptionid', 'SubscriptionId') &&
                        this.orStringCompare(header[9], 'subscriptionname', "Nome della sottoscrizione") &&
                        this.orStringCompare(header[10], 'date', 'Data') &&
                        this.orStringCompare(header[11], 'product', 'Prodotto') &&
                        this.orStringCompare(header[12], 'partnumber', 'PartNumber') &&
                        this.orStringCompare(header[13], 'meterid', 'ID del contatore') &&
                        this.orStringCompare(header[14], 'servicefamily', 'FamigliaDiServizi') &&
                        this.orStringCompare(header[15], 'metercategory', 'Categoria del contatore') &&
                        this.orStringCompare(header[16], 'metersubcategory', 'MeterSubcategory') &&
                        this.orStringCompare(header[17], 'meterregion', 'Area del contatore') &&
                        this.orStringCompare(header[18], 'metername', 'Nome del contatore') &&
                        this.orStringCompare(header[19], 'quantity', 'Quantità') &&
                        this.orStringCompare(header[20], 'effectiveprice', 'PrezzoValido') &&
                        this.orStringCompare(header[21], 'cost', 'Costo') &&
                        this.orStringCompare(header[22], 'unitprice', 'UnitPrice') &&
                        this.orStringCompare(header[23], 'billingcurrency', 'ValutaFatturazione') &&
                        this.orStringCompare(header[24], 'resourcelocation', 'Posizione della risorsa') &&
                        this.orStringCompare(header[25], 'availabilityzone', 'ZonaDiDisponibilità') &&
                        this.orStringCompare(header[26], 'consumedservice', 'Servizio utilizzato') &&
                        this.orStringCompare(header[27], 'resourceid', 'ResourceId') &&
                        this.orStringCompare(header[28], 'resourcename', 'NomeRisorsa') &&
                        this.orStringCompare(header[29], 'serviceinfo1', 'Informazioni sul servizio 1') &&
                        this.orStringCompare(header[30], 'serviceinfo2', 'Informazioni sul servizio 2') &&
                        this.orStringCompare(header[31], 'additionalinfo', 'Informazioni aggiuntive') &&
                        this.orStringCompare(header[32], 'tags', 'Tag') &&
                        this.orStringCompare(header[33], 'invoicesectionid', 'InvoiceSectionId') &&
                        this.orStringCompare(header[34], 'invoicesection', 'SezioneFattura') &&
                        this.orStringCompare(header[35], 'costcenter', 'Centro di costo') &&
                        this.orStringCompare(header[36], 'unitofmeasure', 'Unità di misura') &&
                        this.orStringCompare(header[37], 'resourcegroup', 'Gruppo di risorse') &&
                        this.orStringCompare(header[38], 'reservationid', 'IDPrenotazione') &&
                        this.orStringCompare(header[39], 'reservationname', 'NomePrenotazione') &&
                        this.orStringCompare(header[40], 'productorderid', 'IDOrdineProdotto') &&
                        this.orStringCompare(header[41], 'productordername', 'NomeOrdineProdotto') &&
                        this.orStringCompare(header[42], 'offerid', 'OfferId') &&
                        this.orStringCompare(header[43], 'isazurecrediteligible', 'ÈIdoneoPerCreditoAzure') &&
                        this.orStringCompare(header[44], 'term', 'Termine') &&
                        this.orStringCompare(header[45], 'publishername', 'PublisherName') &&
                        this.orStringCompare(header[46], 'planname', 'PlanName') &&
                        this.orStringCompare(header[47], 'chargetype', 'ChargeType') &&
                        this.orStringCompare(header[48], 'frequency', 'Frequenza') &&
                        this.orStringCompare(header[49], 'publishertype', 'TipoDiAutore') &&
                        this.orStringCompare(header[50], 'pricingmodel', 'PricingModel') &&
                        this.orStringCompare(header[51], 'paygprice', 'PayGPrice') &&
                        this.orStringCompare(header[52], 'invoiceid', 'InvoiceID')) {
                        return true;
                    } else {
                        this.errorText = MessageResourceManager.Current.getMessage('Error_Message_Headers');
                        return false;
                    }

                } else {
                    this.errorText = MessageResourceManager.Current.getMessage('Error_Message_NoContent');
                    return false;
                }


            } else {
                this.errorText = MessageResourceManager.Current.getMessage('Error_Message_Splitter');
                return false;
            }



        } else {
            this.errorText = MessageResourceManager.Current.getMessage('NotValidFormat');
            return false;
        }
    }

    stringCompare(str1: string, str2: string): boolean {
        return str1.toLocaleLowerCase().includes(str2.toLocaleLowerCase());
    }

    orStringCompare(str1: string, str2: string, str3: string): boolean {
        return this.stringCompare(str1, str2) || this.stringCompare(str1, str3);
    }

    readFileAsync(file) {
        return new Promise<String>((resolve, reject) => {
            let reader = new FileReader();

            reader.onload = () => {
                resolve(reader.result as String);
            };

            reader.onerror = reject;

            reader.readAsText(file);
        })
    }

    isCsv(file: File): boolean {
        return file?.type?.indexOf('csv') > 0;
    }

    canUploadFile(): Observable<boolean> {
        return merge(
            this.showProgressBar$,
            this.files$
        ).pipe(map(() => {
            return this.files$.value != null &&
                Array.from(this.files$.value).length > 0 &&
                !this.showProgressBar$.value
        }))
    }
}
